/**
 * Algolia localStorage cache wrapper
 * Consolidates duplicate cache logic from 3 verification JS files.
 * Parameterizes CACHE_KEY, TTL, and MAX_ENTRIES for per-page isolation.
 */

export interface AlgoliaCacheOptions {
  cacheKey?: string;
  ttlMs?: number;
  maxEntries?: number;
}

interface CacheEntry {
  data: unknown;
  ts: number;
}

type CacheStore = Record<string, CacheEntry>;

interface SearchClient {
  search: (requests: unknown[]) => Promise<unknown>;
  [key: string]: unknown;
}

const DEFAULT_CACHE_KEY = 'algolia-responses';
const DEFAULT_TTL_MS = 300 * 1000; // 5 minutes
const DEFAULT_MAX_ENTRIES = 100;

/**
 * 時刻由来フィルタ（`*_unix > <epoch>`）はリクエスト毎に閾値が変わり、同一内容の
 * 再検索でもキャッシュキーが変わってヒットを取りこぼす。キーからは閾値を定数へ
 * 正規化して安定させ、実クエリ（originalSearch には生の requests を渡す）は実時刻を保持する。
 * 鮮度は TTL で担保する。
 *
 * 対象は buildSystemFilters が付与する時刻フィルタ＝`meta.variant.sales_deadline_unix` /
 * `meta.variant.expiration_date_unix` の 2 つ（いずれも unix 秒の単調増加閾値）。
 * ⚠️ 正規表現は `_unix > <数値>` に総称マッチする。`*_unix` は「unix 時刻閾値」である前提。
 *    将来 `_unix >` 比較で**時刻以外の意味**を持つフィルタを追加する場合、同一キャッシュから
 *    誤った結果を返さないよう、ここを明示フィールド名（sales_deadline_unix|expiration_date_unix）
 *    に限定するなど対象を見直すこと。
 */
function computeCacheKey(requests: unknown[]): string {
  return JSON.stringify(requests).replace(/(_unix\s*>=?\s*)\d+/g, '$1T');
}

function getStore(cacheKey: string): CacheStore {
  try {
    const raw = localStorage.getItem(cacheKey);
    return raw ? (JSON.parse(raw) as CacheStore) : {};
  } catch {
    return {};
  }
}

function evictOldest(store: CacheStore, count: number): void {
  const entries = Object.keys(store)
    .map((k) => ({ key: k, ts: store[k].ts }))
    .sort((a, b) => a.ts - b.ts);
  for (let i = 0; i < Math.min(count, entries.length); i++) {
    delete store[entries[i].key];
  }
}

function setStore(cacheKey: string, store: CacheStore, maxEntries: number): void {
  try {
    localStorage.setItem(cacheKey, JSON.stringify(store));
  } catch {
    // QuotaExceeded — evict oldest entries and retry
    evictOldest(store, Math.floor(maxEntries / 2));
    try {
      localStorage.setItem(cacheKey, JSON.stringify(store));
    } catch {
      // give up
    }
  }
}

/**
 * Wrap an Algolia search client with localStorage caching.
 *
 * @param client - Raw Algolia search client
 * @param options - Cache configuration
 * @returns Wrapped search client with caching
 */
export function wrapWithLocalStorageCache(
  client: SearchClient,
  options: AlgoliaCacheOptions = {},
): SearchClient {
  const cacheKey = options.cacheKey ?? DEFAULT_CACHE_KEY;
  const ttlMs = options.ttlMs ?? DEFAULT_TTL_MS;
  const maxEntries = options.maxEntries ?? DEFAULT_MAX_ENTRIES;

  const originalSearch = client.search.bind(client);

  return { ...client, search(requests: unknown[]): Promise<unknown> {
      const key = computeCacheKey(requests);
      const store = getStore(cacheKey);
      const entry = store[key];

      if (entry && Date.now() - entry.ts < ttlMs) {
        // Cache HIT — dispatch event for debug counter
        document.dispatchEvent(new CustomEvent('algolia-cache-hit'));
        return Promise.resolve(entry.data);
      }

      // Cache MISS
      return originalSearch(requests).then((response: unknown) => {
        const s = getStore(cacheKey);
        if (Object.keys(s).length >= maxEntries) {
          evictOldest(s, 20);
        }
        s[key] = { data: response, ts: Date.now() };
        setStore(cacheKey, s, maxEntries);
        return response;
      });
    },};
}
